import { reactive, readonly } from "vue";
import { CenterWithPermission, Role, RolePermission, Token, User, UserInfo } from "@/utils/models/user.model";
import API from "@/services/api/api.service";
import { PermissionAction } from "./const/permission_actions";
import { defaultLanguage, i18n } from '@/assets/i18n/i18n';
import config from "./const/config";
import LangStorage from "./methods/lang_storage";
import { Locale } from "./const/env";

interface StateStore {
  user: User | null;
  init(): Promise<void>;
  login(token_info: Token): void;
  introspection(user_info: UserInfo): void;
  logout(): void;
  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
  center: () => CenterWithPermission | null;
  centers: () => CenterWithPermission[];
  getCenter(id: number): CenterWithPermission | undefined;
  isPasswordExpired: () => boolean;
  isContentManager: () => boolean;
  updateUserInfo(): Promise<void>;
  getPermisison(action: PermissionAction): boolean;
  getCentersWithPermisison(action: PermissionAction): CenterWithPermission[] | undefined;
  getCenterPermission(center_id: number, action: PermissionAction): boolean
}

const allowedRoles = [Role.admin, Role.hcp, Role.internal];

const stateStore: StateStore = reactive({
  user: null,
  async init() {
    const storedUser = localStorage.getItem('user');
    if (storedUser != null) {
      const user = JSON.parse(storedUser);
      stateStore.user = user;

      await this.updateUserInfo();
    }
  },
  login(token_info) {
    stateStore.user = {} as User;
    stateStore.user.token = token_info;
    localStorage.setItem('user', JSON.stringify(stateStore.user!));
  },
  introspection(user_info) {
    stateStore.user!.info = user_info;
    localStorage.setItem('user', JSON.stringify(stateStore.user!));
    i18n.global.locale = (user_info.preference?.language_code as Locale | undefined) ?? defaultLanguage();
    console.log('LOCAL_LANG', i18n.global.locale);
    LangStorage.set(i18n.global.locale);
  },
  async logout() {
    stateStore.user = null;
    localStorage.removeItem('user');
  },
  async updateUserInfo() {
    try {
      const res = await API.introspection();
      this.introspection(res.data);
    } catch (error) {
      this.logout();
    };
  },
  isAuthenticated(): boolean { return localStorage.getItem('user') !== null && stateStore.user !== null && stateStore.user.token !== null && stateStore.user.info !== null && allowedRoles.includes(stateStore.user.info.role.type); },
  isAdmin(): boolean { return (this.user as User | null)?.info.is_admin ?? false },
  center(): CenterWithPermission | null {
    var center: CenterWithPermission | null = null;
    var centers = stateStore.user?.info.role.centers;
    if (centers !== undefined && centers.length !== 0) {
      center = centers![0];
    }
    return center;
  },
  centers(): CenterWithPermission[] {
    return stateStore.user?.info.role.centers ?? [];
  },
  getCenter(id: number): CenterWithPermission | undefined {
    return stateStore.user?.info.role.centers?.find((center) => center.id === id);
  },
  isPasswordExpired() {
    return stateStore.user?.info.is_password_expired ?? false;
  },
  isContentManager() {
    return stateStore.user?.info.is_contentmanager ?? false;
  },
  getPermisison(action): boolean {
    return this.centers().some((center) => center.pivot[action] == true);
  },
  getCentersWithPermisison(action): CenterWithPermission[] | undefined {
    return this.centers().filter((center) => center.pivot[action] == true);
  },
  getCenterPermission(center_id, action): boolean {
    return this.centers().find((center) => center.id === center_id)?.pivot[action] == true;
  }
})

export default stateStore